import { useTheme } from '@emotion/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Divider, Typography, useMediaQuery } from '@mui/material';
import CustomSnackBar from 'components/Common/CustomSnackBar';
import PasswordField from 'components/Common/LabelledTextField/PasswordField';
import { getLocalStorageItem } from 'hooks';
import { useChangePasswordApiQuery } from 'queries/ChangePassword';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import HandleErrorResponse from 'utils/HandleErrorResponse';
import * as yup from 'yup';

const ChangePassword: React.FC = () => {
  const { t } = useTranslation('account');
  const { t: translate } = useTranslation('validationErrorMessages');
  const newPasswordText = translate('dynamicFieldValue.newPassword');
  const confirmPasswordText = translate('dynamicFieldValue.confirmPassword');
  const currentPasswordText = translate('dynamicFieldValue.currentPassword');
  const userPasswordSchema = yup.object().shape({
    newPassword: yup
      .string()
      .nullable()
      .required(() =>
        translate('requiredErrorMessage', {
          field: newPasswordText,
        })
      )
      .min(8, () =>
        translate('minErrorMessage', { field: newPasswordText, minValue: 8 })
      )
      .matches(/^\S*$/, () => translate('whiteSpaceErrorMessage'))
      .max(32, () =>
        translate('maxErrorMessage', { field: newPasswordText, maxValue: 32 })
      )
      .matches(/([a-z])/, {
        message: () => translate('atleastOneSmallLetter'),
        name: 'small letter',
        excludeEmptyString: true,
      })
      // 'Atleast one small letter'
      .matches(/[A-Z]/, () => translate('atleastOneCapitalLetter'))
      .matches(/(\d)/, () => translate('atleastOneNumber'))
      .matches(/(\W)/, () => translate('oneSpecialCharacter')),
    confirmPassword: yup
      .string()
      .nullable()
      .required(() =>
        translate('requiredErrorMessage', {
          field: confirmPasswordText,
        })
      )
      .matches(/^\S*$/, () => translate('whiteSpaceErrorMessage'))
      // eslint-disable-next-line consistent-return
      .when('newPassword', (newPassword, schema): any => {
        if (newPassword)
          return schema
            .required(() =>
              translate('requiredErrorMessage', {
                field: confirmPasswordText,
              })
            )
            .test(
              'Match password',
              `${translate('passwordNotMatch')}`,
              (confirmPasswordValue: string | null, formValues: any) => {
                return confirmPasswordValue === formValues.parent.newPassword;
              }
            );
      }),
    currentPassword: yup
      .string()
      .nullable()
      .required(() =>
        translate('requiredErrorMessage', {
          field: currentPasswordText,
        })
      ),
  });

  const {
    register,
    handleSubmit,
    setError,
    reset,
    trigger,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(userPasswordSchema),
  });
  const theme: any = useTheme();
  const underSm = useMediaQuery(theme.breakpoints.down('sm'));
  const { mutate: changePasswordApi } = useChangePasswordApiQuery();
  const [openSnackBar, setopenSnackBar] = useState(false);
  const [buttonEnable, setButtonEnable] = useState<boolean>(false);

  // handlers
  const submitAuthForm = (data: any) => {
    const accountInfo = getLocalStorageItem('roles');
    const payload: any = {
      data: {
        currentPassword: data?.currentPassword.toString(),
        newPassword: data?.newPassword,
        confirmPassword: data?.confirmPassword.toString(),
      },
      id: accountInfo?.account_id,
    };
    changePasswordApi(payload, {
      onSuccess: () => {
        setopenSnackBar(true);
        reset({
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
      },
      onError: (err: any) => {
        HandleErrorResponse(err, setError);
      },
    });
  };
  const handleCloseSnackBar = () => {
    setopenSnackBar(false);
  };
  const [newPassword, confirmPassword] = watch([
    'newPassword',
    'confirmPassword',
  ]);
  useEffect(() => {
    if (confirmPassword) {
      trigger('confirmPassword');
    }
  }, [newPassword, trigger]);
  const handleResetInfo = () => {
    reset({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
  };

  const handleAuthErrors = (err: any) => {};

  return (
    <div className={`pt-2 ${!underSm ? 'h-[70vh] overflow-y-auto' : ''}`}>
      <form onSubmit={handleSubmit(submitAuthForm, handleAuthErrors)}>
        <div className="mb-5">
          <Typography fontSize={18} fontWeight={600} color="secondary.100">
            {t('password')}
          </Typography>

          <Typography variant="subtitle2" fontWeight={400} color="font.900">
            {t('passwordInstruction')}
          </Typography>
        </div>
        <Divider />

        <div className="mb-5 mt-6 flex flex-col gap-4 md:flex-row md:items-center">
          <Typography
            variant="subtitle2"
            fontWeight={500}
            color="font.main"
            className="md:w-[280px]"
          >
            {t('currentPassword')} *
          </Typography>
          <PasswordField
            label=""
            placeholder={`${t(`currentPasswordPlaceholder`)}`}
            {...register('currentPassword')}
            error={Boolean(errors.currentPassword)}
            helperText={errors?.currentPassword?.message}
            onBlur={() => setButtonEnable(true)}
            className="md:w-[512px]"
          />
        </div>

        <Divider />

        <div className="mb-5 mt-6 flex flex-col gap-4 md:flex-row md:items-center">
          <Typography
            variant="subtitle2"
            fontWeight={500}
            color="font.main"
            className="md:w-[280px]"
          >
            {t('newPassword')} *
          </Typography>
          <PasswordField
            label=""
            placeholder={`${t('newPasswordPlaceholder')}`}
            {...register('newPassword')}
            error={Boolean(errors.newPassword)}
            helperText={errors?.newPassword?.message}
            onBlur={() => setButtonEnable(true)}
            className="md:w-[512px]"
          />
        </div>

        <Divider />

        <div className="mb-5 mt-6 flex flex-col gap-4 md:flex-row md:items-center">
          <Typography
            variant="subtitle2"
            fontWeight={500}
            color="font.main"
            className="md:w-[280px]"
          >
            {t('confirmNewPassword')} *
          </Typography>
          <PasswordField
            label=""
            placeholder={`${t('enterConfirmPassword')}`}
            {...register('confirmPassword')}
            error={Boolean(errors.confirmPassword)}
            helperText={errors?.confirmPassword?.message}
            onBlur={() => setButtonEnable(true)}
            className="md:w-[512px]"
          />
        </div>

        <div className={`${underSm ? 'mb-[50px]' : ''} flex justify-end pr-2`}>
          <div className="flex gap-3">
            <Button
              variant="outlined"
              color="font"
              onClick={handleResetInfo}
              disabled={!buttonEnable}
              className={`${
                !buttonEnable ? 'cursor-not-allowed !bg-[#90cdf4]' : ''
              }`}
            >
              {t('cancelButtonText')}
            </Button>

            <Button
              type="submit"
              disabled={!buttonEnable}
              className={`bg-[#11AC9E] hover:bg-[#90BEE6] ${
                !buttonEnable ? 'cursor-not-allowed !bg-[#90cdf4]' : ''
              }`}
            >
              {t('updatePassword')}
            </Button>
          </div>
        </div>
      </form>
      <CustomSnackBar
        shouldOpen={openSnackBar}
        handleCloseSnackBar={handleCloseSnackBar}
        text="Password changed Successfully"
        width="auto !important"
      />
    </div>
  );
};

export default ChangePassword;
